import { gql } from "lib/apolloClient";
import {
  progressReportFragment,
  progressReportTemplateFragment,
  progressReportRatingFragment,
  progressReportRemarkFragment,
  studentProgressReportFragment,
  progressReportTaskGroupFragment,
  aiVoiceTonesFragment,
  commentTemplatesFragment,
} from "./ProgressReportFragments";
import { staffFragment } from "modules/CommonFragments";

export const createProgressReportTemplateMutation = gql`
  mutation createProgressReportTemplate(
    $name: String!
    $grades: [String!]
    $body: JSON!
    $curriculumProgramId: ID
    $gradingPeriodType: PROGRESS_REPORT_GRADING_PERIOD_TYPE_ENUM
    $academicYearId: ID
  ) {
    documentation {
      createProgressReportTemplate(
        input: {
          name: $name
          grades: $grades
          body: $body
          curriculumProgramId: $curriculumProgramId
          gradingPeriodType: $gradingPeriodType
          academicYearId: $academicYearId
        }
      ) {
        ...progressReportTemplateItem
      }
    }
  }
  ${progressReportTemplateFragment.progressReportTemplateFeed}
`;

export const copyProgressReportTemplateMutation = gql`
  mutation copyProgressReportTemplate(
    $input: CopyProgressReportTemplateInput!
  ) {
    documentation {
      copyProgressReportTemplate(input: $input) {
        ...progressReportTemplateItem
      }
    }
  }
  ${progressReportTemplateFragment.progressReportTemplateFeed}
`;

export const importProgressReportTemplateMutation = gql`
  mutation importProgressReportTemplate(
    $input: ImportProgressReportTemplateInput!
  ) {
    documentation {
      importProgressReportTemplate(input: $input) {
        ...progressReportTemplateItem
      }
    }
  }
  ${progressReportTemplateFragment.progressReportTemplateFeed}
`;

export const updateProgressReportTemplateMutation = gql`
  mutation updateProgressReportTemplate(
    $id: ID!
    $name: String
    $grades: [String!]
    $body: JSON
    $isArchived: Boolean
  ) {
    documentation {
      updateProgressReportTemplate(
        input: {
          name: $name
          grades: $grades
          body: $body
          id: $id
          isArchived: $isArchived
        }
      ) {
        ...progressReportTemplateItem
      }
    }
  }
  ${progressReportTemplateFragment.progressReportTemplateFeed}
`;

export const deleteProgressReportTemplateMutation = gql`
  mutation deleteProgressReportTemplate($id: ID!) {
    documentation {
      deleteProgressReportTemplate(input: { id: $id }) {
        isSuccess
        warning
        error {
          status
        }
      }
    }
  }
`;

export const createCourseProgressReportTaskGroupMutation = gql`
  mutation createCourseProgressReportTaskGroup(
    $input: ProgressReportTaskGroupInput!
  ) {
    documentation {
      createProgressReportGroup(input: $input) {
        ...progressReportTaskGroupItem
      }
    }
  }
  ${progressReportTaskGroupFragment.ProgressReportTaskGroupFeed}
`;

export const updateCourseProgressReportTaskMutation = gql`
  mutation updateCourseProgressReportTask(
    $id: ID!
    $progressReportTemplateId: ID
    $addedUnitPlanIds: [ID!]
    $removedUnitPlanIds: [ID!]
    $unitPlanIds: [ID!]
    $addedStudents: [ID!]
    $removedStudents: [ID!]
    $title: String
    $deadlineDate: String
    $startDate: String
    $endDate: String
    $signatoriesGroup: SignatoriesGroupInput
  ) {
    documentation {
      updateCourseProgressReportTask(
        input: {
          id: $id
          progressReportTemplateId: $progressReportTemplateId
          addedUnitPlanIds: $addedUnitPlanIds
          removedUnitPlanIds: $removedUnitPlanIds
          addedStudents: $addedStudents
          removedStudents: $removedStudents
          unitPlanIds: $unitPlanIds
          title: $title
          deadlineDate: $deadlineDate
          attendanceDates: { startDate: $startDate, endDate: $endDate }
          signatoriesGroup: $signatoriesGroup
        }
      ) {
        ...progressReportItem
      }
    }
  }
  ${progressReportFragment.progressReportFeed}
`;

export const updateCourseProgressReportGroupMutation = gql`
  mutation updateCourseProgressReportGroup(
    $input: ProgressReportTaskGroupUpdateInput!
  ) {
    documentation {
      updateProgressReportGroup(input: $input) {
        showWarning
        warning
        progressReportTaskGroup {
          ...progressReportTaskGroupItem
        }
      }
    }
  }
  ${progressReportTaskGroupFragment.ProgressReportTaskGroupFeed}
`;

export const deleteCourseProgressReportTaskMutation = gql`
  mutation deleteCourseProgressReportTask($id: ID!) {
    documentation {
      deleteCourseProgressReportTask(input: { id: $id }) {
        isSuccess
        warning
      }
    }
  }
`;

export const deleteCourseProgressReportGroupMutation = gql`
  mutation deleteCourseProgressReportGroup($id: ID!) {
    documentation {
      deleteProgressReportGroup(input: { id: $id })
    }
  }
`;

export const upsertStudentProgressReportPypElementRatingMutation = gql`
  mutation upsertStudentProgressReportPypElementRating(
    $nodeId: ID!
    $studentProgressReportId: ID!
    $academicCriteriaValueId: ID!
    $academicCriteriaSetId: ID!
    $ibPYPElementId: ID
    $ibPYPElementType: IB_PYP_ELEMENT_RATING_TYPE_ENUM!
    $academicTermId: ID
  ) {
    documentation {
      upsertStudentProgressReportPypElementRating(
        input: {
          nodeId: $nodeId
          studentProgressReportId: $studentProgressReportId
          academicCriteriaValueId: $academicCriteriaValueId
          academicCriteriaSetId: $academicCriteriaSetId
          ibPYPElementId: $ibPYPElementId
          ibPYPElementType: $ibPYPElementType
          academicTermId: $academicTermId
        }
      ) {
        ...progressReportRatingItem
      }
    }
  }
  ${progressReportRatingFragment.progressReportRating}
`;

export const removeStudentProgressReportPypElementRatingMutation = gql`
  mutation removeStudentProgressReportPypElementRating(
    $nodeId: ID!
    $studentProgressReportId: ID!
    $academicCriteriaSetId: ID!
    $academicCriteriaValueId: ID!
    $ibPYPElementId: ID
    $ibPYPElementType: IB_PYP_ELEMENT_RATING_TYPE_ENUM!
    $academicTermId: ID
  ) {
    documentation {
      removeStudentProgressReportIBPYPElementRating(
        input: {
          nodeId: $nodeId
          studentProgressReportId: $studentProgressReportId
          academicCriteriaSetId: $academicCriteriaSetId
          academicCriteriaValueId: $academicCriteriaValueId
          ibPYPElementId: $ibPYPElementId
          ibPYPElementType: $ibPYPElementType
          academicTermId: $academicTermId
        }
      )
    }
  }
`;

export const upsertStudentProgressReportRemarkMutation = gql`
  mutation upsertStudentProgressReportRemark(
    $nodeId: ID!
    $studentProgressReportId: ID!
    $remarkType: String
    $message: String
    $attachments: GenericAttachmentGroupInput
  ) {
    documentation {
      upsertStudentProgressReportRemark(
        input: {
          nodeId: $nodeId
          message: $message
          studentProgressReportId: $studentProgressReportId
          attachments: $attachments
          remarkType: $remarkType
        }
      ) {
        ...progressReportRemarkItem
      }
    }
  }
  ${progressReportRemarkFragment.progressReportRemark}
`;

export const updateStudentReflectionStatusMutation = gql`
  mutation updateStudentReflectionStatus(
    $studentProgressReportIds: [ID!]!
    $studentReflectionStatus: STUDENT_REFLECTION_STATUS_ENUM!
  ) {
    documentation {
      updateStudentReflectionStatus(
        input: {
          studentProgressReportIds: $studentProgressReportIds
          studentReflectionStatus: $studentReflectionStatus
        }
      ) {
        isSuccess
        warning
        studentProgressReportId
      }
    }
  }
`;

export const updateStudentReflectionFeedbackMutation = gql`
  mutation UpdateStudentReflectionFeedback(
    $input: UpdateStudentReflectionFeedbackInput!
  ) {
    documentation {
      updateStudentReflectionFeedback(input: $input) {
        isSuccess
        warning
        studentProgressReportId
      }
    }
  }
`;

export const updateStudentProgressReportStatusV2Mutation = gql`
  mutation updateStudentProgressReportStatusV2(
    $bulkStudentProgressReportStateUpdateInput: BulkStudentProgressReportStateUpdateInput!
  ) {
    documentation {
      updateStudentProgressReportStateV2(
        input: $bulkStudentProgressReportStateUpdateInput
      ) {
        ...studentProgressReportStatusItem
      }
    }
  }
  ${studentProgressReportFragment.studentProgressReportStatus}
`;

export const updateStudentProgressReportAttendanceMutation = gql`
  mutation updateStudentProgressReportAttendance(
    $input: UpdateStudentProgressReportAttendanceInputV2!
  ) {
    documentation {
      updateStudentProgressReportAttendanceV2(input: $input) {
        id
      }
    }
  }
`;

export const updateCourseProgressReportStateMutation = gql`
  mutation updateCourseProgressReportState(
    $id: ID!
    $fromStatus: STUDENT_PROGRESS_REPORT_STATUS
    $status: STUDENT_PROGRESS_REPORT_STATUS!
    $direction: DIRECTION_ENUM!
    $shouldNotifyPublished: Boolean
  ) {
    documentation {
      updateCourseProgressReportState(
        input: {
          id: $id
          fromStatus: $fromStatus
          status: $status
          direction: $direction
          shouldNotifyPublished: $shouldNotifyPublished
        }
      ) {
        ...progressReportItem
      }
    }
  }
  ${progressReportFragment.progressReportFeed}
`;

export const updateCourseProgressReportTaskPypElementsMutation = gql`
  mutation updateCourseProgressReportTaskPypElements(
    $taskId: ID!
    $parentType: COURSE_PROGRESS_REPORT_TASK_PYP_ELEMENT_PARENT_ENUM!
    $parentId: ID
    $addedElements: [ProgressReportPypElementInput!]
    $removedElements: [ProgressReportPypElementInput!]
  ) {
    documentation {
      updateCourseProgressReportTaskPypElements(
        input: {
          taskId: $taskId
          parentType: $parentType
          parentId: $parentId
          addedElements: $addedElements
          removedElements: $removedElements
        }
      )
    }
  }
`;

export const updatePlannerElementsForStudentProgressReportMutation = gql`
  mutation updatePlannerElementsForStudentProgressReport(
    $input: UpdatePlannerElementsForStudentProgressReportInput!
  ) {
    documentation {
      updatePlannerElementsForStudentProgressReport(input: $input)
    }
  }
`;

export const quickCopyProgressReportRemarkMutation = gql`
  mutation quickCopyProgressReportRemark(
    $input: QuickCopyStudentProgressReportRemarkInput!
  ) {
    documentation {
      quickCopyStudentProgressReportRemark(input: $input)
    }
  }
`;

export const markStudentProgressReportSectionCompleteMutation = gql`
  mutation markStudentProgressReportSectionComplete(
    $studentProgressReportId: ID!
    $sections: UpdateArrayInput
  ) {
    documentation {
      markStudentProgressReportSectionComplete(
        input: {
          studentProgressReportId: $studentProgressReportId
          sections: $sections
        }
      )
    }
  }
`;
export const upsertUnitPlansForStudentProgressReportMutation = gql`
  mutation upsertUnitPlansForStudentProgressReport(
    $input: UpsertUnitPlansForStudentProgressReportsInput!
  ) {
    documentation {
      upsertUnitPlansForStudentProgressReports(input: $input)
    }
  }
`;

export const updateEntityStaffMappingForStudentProgressReportMutation = gql`
  mutation updateEntityStaffMappingForStudentProgressReport(
    $input: UpdateEntityStaffMappingForStudentProgressReportInput!
  ) {
    documentation {
      updateEntityStaffMappingForStudentProgressReport(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const updateSignatoryMappingForStudentProgressReportMutation = gql`
  mutation updateSignatoryMappingForStudentProgressReport(
    $input: UpdateSignatoryMappingForStudentProgressReportInput!
  ) {
    documentation {
      updateSignatoryMappingForStudentProgressReport(input: $input) {
        isSuccess
        warning
      }
    }
  }
`;

export const updateCoverSheetCustomBackgroundUrlMutation = gql`
  mutation updateCoverSheetCustomBackgroundUrl(
    $id: ID!
    $coverSheetBackgroundUrl: String
  ) {
    documentation {
      updateStudentProgressReport(
        input: { id: $id, coverSheetBackgroundUrl: $coverSheetBackgroundUrl }
      ) {
        id
        status
      }
    }
  }
`;

export const createAIVoiceToneMutation = gql`
  mutation createAiVoiceTone($input: VoiceToneCreateInput!) {
    ai {
      createAiVoiceTone(input: $input) {
        isSuccess
        response {
          id
          ...progressReportAIVoiceTones
        }
      }
    }
  }
  ${aiVoiceTonesFragment.progressReportAIVoiceTones}
`;

export const updateAIVoiceToneMutation = gql`
  mutation updateAiVoiceTone($input: [VoiceToneEditInput!]) {
    ai {
      updateAiVoiceTone(input: $input) {
        isSuccess
        response {
          id
          ...progressReportAIVoiceTones
        }
      }
    }
  }
  ${aiVoiceTonesFragment.progressReportAIVoiceTones}
`;

export const deleteAIVoiceToneMutation = gql`
  mutation deleteAiVoiceTone($input: VoiceToneDeleteInput!) {
    ai {
      deleteAiVoiceTone(input: $input)
    }
  }
`;

export const createUserVoiceToneSelectionMutation = gql`
  mutation createUserVoiceToneSelection($input: UserVoiceToneSelectionInput!) {
    ai {
      createUserVoiceToneSelection(input: $input)
    }
  }
`;

export const deleteUserVoiceToneSelectionMutation = gql`
  mutation deleteUserVoiceToneSelection(
    $input: UserVoiceToneSelectionRemoveInput!
  ) {
    ai {
      deleteUserVoiceToneSelection(input: $input)
    }
  }
`;

export const generateProgressReportsZipFileUrlMutation = gql`
  mutation generateProgressReportsZipFileUrl(
    $input: ProgressReportsZipFileUrlInput!
  ) {
    documentation {
      generateProgressReportsZipFileUrl(input: $input) {
        isSuccess
        isLongRunningTaskCreated
        fileUrl
        fileName
      }
    }
  }
`;

export const createCommentMutation = gql`
  mutation createGradingCommentTemplate(
    $input: CreateGradingCommentTemplateInput!
  ) {
    documentation {
      createGradingCommentTemplate(input: $input) {
        ...commentTemplates
      }
    }
  }
  ${commentTemplatesFragment.commentTemplates}
`;

export const updateCommentMutation = gql`
  mutation editGradingCommentTemplate(
    $input: EditGradingCommentTemplateInput!
  ) {
    documentation {
      editGradingCommentTemplate(input: $input) {
        ...commentTemplates
      }
    }
  }
  ${commentTemplatesFragment.commentTemplates}
`;

export const deleteCommentMutation = gql`
  mutation deleteGradingCommentTemplates(
    $input: DeleteGradingCommentTemplatesInput!
  ) {
    documentation {
      deleteGradingCommentTemplates(input: $input) {
        isSuccess
      }
    }
  }
`;

export const bulkUpdateCommentTemplateMutation = gql`
  mutation bulkUpdateGradingCommentTemplate(
    $input: BulkUpdateGradingCommentTemplateInput!
  ) {
    documentation {
      bulkUpdateGradingCommentTemplate(input: $input) {
        isSuccess
      }
    }
  }
`;

export const validateGradingCommentTemplateExcelMutation = gql`
  mutation validateGradingCommentTemplateExcel(
    $input: ValidateGradingCommentTemplateExcelInput!
  ) {
    documentation {
      validateGradingCommentTemplateExcel(input: $input) {
        commentTemplatesDifference
        transactionId
        errors {
          entityName
          errorType
          rowNumber
        }
      }
    }
  }
`;

export const commitGradingCommentTemplateExcelMutation = gql`
  mutation commitGradingCommentTemplateExcel(
    $input: CommitGradingCommentTemplateExcelInput!
  ) {
    documentation {
      commitGradingCommentTemplateExcel(input: $input) {
        isSuccess
      }
    }
  }
`;

export const bulkShareGradingCommentTemplateMutation = gql`
  mutation shareGradingCommentTemplate(
    $input: ShareGradingCommentTemplatesExcelInput!
  ) {
    documentation {
      shareGradingCommentTemplates(input: $input) {
        isSuccess
      }
    }
  }
`;

export const logUserActivityMutation = gql`
  mutation logUserActivity($input: [LogUserActivityInput!]) {
    platform {
      logUserActivity(input: $input)
    }
  }
`;

export const createElementRemarksMutation = gql`
  mutation createElementRemarks($input: [IBPYPElementRemarkInput!]) {
    planner {
      createElementRemarks(input: $input) {
        response {
          id
          createdBy {
            ...staffItem
          }
          createdAt
        }
      }
    }
  }
  ${staffFragment.basicStaffDetails}
`;

export const createProgressReportRatingsMutation = gql`
  mutation createProgressReportRatings($input: [IBPYPElementRatingInput!]) {
    planner {
      createIBPYPElementRatings(input: $input) {
        id
      }
    }
  }
`;

export const deleteProgressReportRatingsByIdsMutation = gql`
  mutation deleteProgressReportRatingsByIds($ids: [ID!]!) {
    planner {
      deleteIBPYPElementRatingByIds(ids: $ids)
    }
  }
`;
